import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { rememberMeasurementId, useConsent } from './consent';
import { pathWithoutLocale } from './locale';
import { settingString, useSettings } from './settings';

/**
 * GA4 — spec §19 ("Analytics: GA4 via Settings").
 *
 * The measurement ID comes from the `analytics.ga4_id` setting, not from a build-time env var,
 * so staff can turn analytics on, swap properties, or switch it off from the admin without a
 * deploy (§6.18). It ships empty, which means a site with nothing configured loads no Google
 * script at all — the default is no third-party request, not a broken one.
 *
 * Why this is client-side rather than injected into the SPA shell: the shell injection
 * (SpaController) only exists in a built deployment, so a server-rendered tag could never be
 * exercised or verified in development. Loading here keeps one code path for both.
 *
 * ⚠️ Nothing here runs without CONSENT. `useAnalytics` gates on lib/consent.ts, so the Google
 * script is not requested at all until the visitor accepts. Do not "optimise" that by loading
 * gtag early and gating the events instead — the request itself is what needs consent.
 */

/** The `gtag` queue GA4 installs on `window`. */
type GtagFn = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFn;
  }
}

/**
 * A GA4 measurement ID is `G-` followed by an alphanumeric property token.
 *
 * Validated because this value is editor-editable and ends up in a `<script src>` on every
 * public page. The host is a fixed literal so the URL cannot be redirected elsewhere, but a
 * pasted-in whole snippet or a Universal Analytics `UA-…` ID would otherwise load a script
 * that 404s on every page view, for nobody's benefit. The server rejects malformed IDs too
 * (SettingUpdateRequest) — this is the second half of that check, since the setting could
 * predate the rule.
 */
const GA4_ID = /^G-[A-Z0-9]{4,20}$/i;

export function isValidGa4Id(id: string): boolean {
  return GA4_ID.test(id);
}

/** Set once the loader has run, so re-renders and locale changes cannot inject a second tag. */
let loadedId: string | null = null;

function loadGtag(id: string): void {
  if (loadedId === id) return;
  loadedId = id;
  // So a later withdrawal can set Google's per-id opt-out flag (see lib/consent.ts).
  rememberMeasurementId(id);

  window.dataLayer = window.dataLayer ?? [];
  // The canonical shim: gtag pushes `arguments` (not an array) onto dataLayer.
  const gtag: GtagFn = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
  };
  window.gtag = gtag;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);

  gtag('js', new Date());
  gtag('config', id, {
    /*
     * The automatic page_view is suppressed and every view — including the first — is sent by
     * the effect below. In an SPA the automatic one fires only on the hard load, so leaving it
     * on would mean the landing page counted twice and every subsequent route not at all.
     */
    send_page_view: false,
    /*
     * Outside production, hits are marked as debug so they land in GA4's DebugView instead of
     * the standard reports. This is deliberately not an "only load in production" check: a
     * staging database cloned from production carries the real measurement ID, and silently
     * dropping those hits would make analytics look broken rather than clean.
     */
    debug_mode: !import.meta.env.PROD,
  });
}

/**
 * Loads GA4 when configured and reports one page_view per SPA navigation.
 *
 * Mounted once, in SiteLayout — the public site only. The admin is a separate bundle and is
 * not tracked: it is staff tooling, and `robots.txt` already keeps it out of search.
 */
export function useAnalytics(): void {
  const { data: settings } = useSettings();
  const { pathname, search } = useLocation();
  const consent = useConsent();
  const id = settingString(settings, 'analytics.ga4_id');

  /*
   * TWO gates, and both must pass: the site has to be configured for analytics AND the visitor
   * has to have granted consent (see lib/consent.ts). While no decision has been made the
   * answer is null, which is not consent — so nothing loads until the visitor chooses, and a
   * visitor who never answers is never tracked.
   */
  const enabled = isValidGa4Id(id) && consent?.analytics === 'granted';

  const isSearch = pathWithoutLocale(pathname) === '/search';

  /*
   * The search page rewrites `?q=` as the visitor types (debounced), so counting the query
   * string as part of the page would log a page_view per partial query — `?q=var`, `?q=vardz`,
   * `?q=vardzia` — and scatter one search across a long tail of distinct page paths. Search is
   * one page view; the term is reported separately as view_search_results below, which is what
   * GA4's site-search report actually reads.
   *
   * Everywhere else the query string IS part of the identity: `?attraction=vardzia` opens a
   * different panel and deserves its own view.
   */
  const page = isSearch ? pathname : pathname + search;

  // Suppresses the duplicate page_view that would otherwise fire when the settings request
  // resolves on the very route that was already reported.
  const lastPage = useRef<string | null>(null);
  const lastTerm = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    loadGtag(id);

    if (lastPage.current === page) return;
    lastPage.current = page;

    window.gtag?.('event', 'page_view', {
      page_path: page,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [enabled, id, page]);

  // GA4's standard site-search event — this is what populates the search-terms report.
  const term = isSearch ? (new URLSearchParams(search).get('q') ?? '').trim() : '';

  useEffect(() => {
    if (!enabled || term === '' || lastTerm.current === term) return;
    lastTerm.current = term;

    window.gtag?.('event', 'view_search_results', { search_term: term });
  }, [enabled, term]);
}
